import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { Card, ClinicaLabel, PageTitle } from "../ui";
import { listarMedicos, criarMedico, atualizarMedico, apagarMedico } from "../../lib/adminP1Api";
import { useAuth } from "../AuthContext";

const EMPTY = {
  nome: "", clinica_principal_slug: "ribeira-brava", foto_url: "",
  bio: "", especialidades: "", ativo: true, ordem_apresentacao: 0,
};

export default function AdminMedicosPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const carregar = async () => {
    setLoading(true);
    setItems(await listarMedicos());
    setLoading(false);
  };
  useEffect(() => { carregar(); }, []);

  const open = (m) => {
    setEditing(m);
    setForm(m
      ? { ...m, especialidades: (m.especialidades || []).join(", ") }
      : EMPTY);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        clinica_principal_slug: form.clinica_principal_slug,
        foto_url: form.foto_url?.trim() || null,
        bio: form.bio?.trim() || null,
        especialidades: form.especialidades.split(",").map((x) => x.trim()).filter(Boolean),
        ativo: !!form.ativo,
        ordem_apresentacao: Number(form.ordem_apresentacao || 0),
      };
      if (editing?.id) {
        await atualizarMedico(editing.id, payload);
        toast.success("Médico atualizado.");
      } else {
        await criarMedico(payload);
        toast.success("Médico criado.");
      }
      close();
      carregar();
    } catch (err) {
      const det = err?.response?.data?.detail;
      const msg = Array.isArray(det) ? det[0]?.msg : det;
      toast.error(msg || "Erro ao guardar.");
    } finally { setSaving(false); }
  };

  const onDelete = async (m) => {
    if (!window.confirm(`Apagar médico "${m.nome}"?`)) return;
    try {
      await apagarMedico(m.id);
      toast.success("Médico apagado.");
      carregar();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao apagar."); }
  };

  const inputCls = "w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-4 py-2.5 text-sm focus:border-[#1E5F6E] focus:outline-none";

  return (
    <div data-testid="admin-medicos">
      <div className="flex items-start justify-between gap-3 mb-6">
        <PageTitle subtitle="Equipa veterinária.">Médicos</PageTitle>
        {canEdit && (
          <button onClick={() => open(null)} data-testid="btn-novo-medico" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#174E5B]">
            <Plus className="h-4 w-4" />
            Novo médico
          </button>
        )}
      </div>

      {loading ? <p className="text-[#5A5A5A]">A carregar...</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.length === 0 && <p className="text-sm text-[#5A5A5A]">Sem médicos registados.</p>}
          {items.map((m) => (
            <Card key={m.id} testId={`medico-${m.id}`}>
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 shrink-0 rounded-2xl overflow-hidden bg-[#1E5F6E]/10 grid place-items-center">
                  {m.foto_url ? (
                    <img src={m.foto_url} alt={m.nome} className="h-full w-full object-cover" />
                  ) : <ImageIcon className="h-6 w-6 text-[#88A096]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-[#1A1A1A]">{m.nome}</div>
                  <div className="text-xs text-[#5A5A5A]"><ClinicaLabel slug={m.clinica_principal_slug} /></div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#88A096] mt-1">
                    {m.ativo ? "Ativo" : "Inativo"}
                  </div>
                </div>
              </div>
              {m.especialidades?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {m.especialidades.map((e) => <span key={e} className="text-[10px] font-semibold rounded-full bg-[#1E5F6E]/8 text-[#1E5F6E] px-2 py-0.5">{e}</span>)}
                </div>
              )}
              {canEdit && (
                <div className="mt-4 flex gap-2">
                  <button onClick={() => open(m)} data-testid={`edit-${m.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-[#1E5F6E]/15 text-[#1E5F6E] px-3 py-1.5 text-xs font-bold">
                    <Pencil className="h-3 w-3" /> Editar
                  </button>
                  <button onClick={() => onDelete(m)} data-testid={`delete-${m.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-rose-200 text-rose-700 px-3 py-1.5 text-xs font-bold">
                    <Trash2 className="h-3 w-3" /> Apagar
                  </button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {editing !== null && canEdit && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={close} data-testid="medico-modal">
          <form onSubmit={submit} className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">{editing?.id ? "Editar médico" : "Novo médico"}</h3>
              <button type="button" onClick={close} className="h-8 w-8 grid place-items-center rounded-lg bg-[#FAF7F2]"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <label className="block"><span className="text-sm font-semibold">Nome *</span>
                <input required data-testid="med-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className={inputCls + " mt-1"} /></label>
              <label className="block"><span className="text-sm font-semibold">Clínica principal</span>
                <select data-testid="med-clinica" value={form.clinica_principal_slug} onChange={(e) => setForm({ ...form, clinica_principal_slug: e.target.value })} className={inputCls + " mt-1"}>
                  <option value="ribeira-brava">Ribeira Brava</option>
                  <option value="calheta">Calheta</option>
                </select></label>
              <label className="block"><span className="text-sm font-semibold">URL da foto</span>
                <input data-testid="med-foto" placeholder="https://.../foto.jpg" value={form.foto_url} onChange={(e) => setForm({ ...form, foto_url: e.target.value })} className={inputCls + " mt-1"} />
                <span className="text-xs text-[#5A5A5A] mt-1 block">Formatos aceites: .jpg, .jpeg, .png, .webp (HTTPS).</span></label>
              <label className="block"><span className="text-sm font-semibold">Bio</span>
                <textarea rows={3} maxLength={600} data-testid="med-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={inputCls + " mt-1"} /></label>
              <label className="block"><span className="text-sm font-semibold">Especialidades</span>
                <input data-testid="med-espec" placeholder="Cardiologia, Cirurgia, ..." value={form.especialidades} onChange={(e) => setForm({ ...form, especialidades: e.target.value })} className={inputCls + " mt-1"} />
                <span className="text-xs text-[#5A5A5A] mt-1 block">Separadas por vírgula.</span></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-sm font-semibold">Ordem</span>
                  <input type="number" data-testid="med-ordem" value={form.ordem_apresentacao} onChange={(e) => setForm({ ...form, ordem_apresentacao: e.target.value })} className={inputCls + " mt-1"} /></label>
                <label className="flex items-center gap-2 pt-7"><input type="checkbox" data-testid="med-ativo" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} className="h-4 w-4 accent-[#1E5F6E]" /> Ativo</label>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={close} className="rounded-full bg-white border-2 border-[#1E5F6E]/15 px-4 py-2 text-sm font-bold text-[#1E5F6E]">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="med-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2 text-sm font-bold disabled:opacity-40">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
